import { useCallback, useEffect, useRef, useState } from 'react';

export interface AsyncState<T> {
  data: T | null;
  error: string | null;
  loading: boolean;
  updatedAt: Date | null;
  reload: () => void;
}

type Loaded<T> = Omit<AsyncState<T>, 'reload'>;
const CACHE_TTL = 5 * 60 * 1000;
// Last successful result per dashboard block, so returning from a review is instant.
const cache = new Map<string, { data: unknown; updatedAt: Date }>();

export function clearDashboardCache() {
  cache.clear();
}

/** When a cached block was last loaded, if it is still fresh. */
export function cachedAt(cacheKey: string): Date | null {
  const entry = cache.get(cacheKey);
  return entry && Date.now() - entry.updatedAt.getTime() < CACHE_TTL ? entry.updatedAt : null;
}

/**
 * Loads data on mount and whenever `key` changes; ignores responses from superseded
 * requests. With `cacheKey`, a result younger than five minutes is reused on mount;
 * changing `key` (the refresh button) always fetches fresh data.
 */
export function useAsyncData<T>(load: () => Promise<T>, key: unknown, cacheKey?: string): AsyncState<T> {
  const cached = cacheKey ? cache.get(cacheKey) : undefined;
  const fresh = cached && Date.now() - cached.updatedAt.getTime() < CACHE_TTL;
  const [state, setState] = useState<Loaded<T>>(() =>
    fresh ? { data: cached!.data as T, error: null, loading: false, updatedAt: cached!.updatedAt } : { data: null, error: null, loading: true, updatedAt: null },
  );
  const request = useRef(0);
  const loader = useRef(load);
  loader.current = load;
  // Key already loaded (or served from cache); survives StrictMode's double effect run.
  const loadedKey = useRef<unknown>(fresh ? key : Symbol('unloaded'));
  const reload = useCallback(() => {
    const id = ++request.current;
    setState((current) => ({ ...current, loading: true, error: null }));
    Promise.resolve().then(() => loader.current()).then(
      (data) => {
        if (id !== request.current) return;
        const updatedAt = new Date();
        if (cacheKey) cache.set(cacheKey, { data, updatedAt });
        setState({ data, error: null, loading: false, updatedAt });
      },
      (error) => {
        console.error('Dashboard data error:', error);
        if (id === request.current) setState({ data: null, error: 'No se pudieron cargar los datos. Intenta actualizar de nuevo.', loading: false, updatedAt: null });
      },
    );
  }, [cacheKey]);
  useEffect(() => {
    if (Object.is(loadedKey.current, key)) return;
    loadedKey.current = key;
    reload();
    return () => {
      request.current++;
      loadedKey.current = Symbol('cancelled');
    };
  }, [reload, key]);
  return { ...state, reload };
}
